/**
 * Launch Promo "Star Hunt" — identity capture, claim API, and the UI
 * wiring for every star button + the single shared popup.
 *
 * Loaded globally (theme.liquid) so the MailerLite ?sid= parameter is
 * captured on whichever page a subscriber lands on first, then persists
 * via localStorage as they browse to find a star elsewhere on the site.
 *
 * Backend is a Cloudflare Worker (see /scratchpad/launch-promo-worker).
 * Still called via JSONP (a <script> tag) rather than fetch() — the
 * Worker does send proper CORS headers so fetch() would work too, but
 * JSONP was already proven out during testing and there's no reason to
 * swap mechanisms on launch day.
 */
(function () {
  var ENDPOINT = 'https://launch-promo-star-hunt.launch-promo-worker.workers.dev';
  var STORAGE_KEY = 're_launch_promo_sid';

  function captureSid() {
    var params = new URLSearchParams(window.location.search);
    var urlSid = params.get('sid');
    if (urlSid) {
      localStorage.setItem(STORAGE_KEY, urlSid);
      return urlSid;
    }
    return localStorage.getItem(STORAGE_KEY);
  }

  function setSid(value) {
    localStorage.setItem(STORAGE_KEY, value);
  }

  function jsonp(url, params, callback, onError) {
    var cbName = 'reLaunchPromoCb' + Date.now() + Math.floor(Math.random() * 1000);
    var timeout = setTimeout(function () {
      cleanup();
      if (onError) onError(new Error('timeout'));
    }, 10000);

    function cleanup() {
      clearTimeout(timeout);
      delete window[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[cbName] = function (data) {
      cleanup();
      callback(data);
    };

    var query = Object.keys(params)
      .map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
      })
      .join('&');

    var script = document.createElement('script');
    script.src = url + '?' + query + '&callback=' + cbName;
    script.onerror = function () {
      cleanup();
      if (onError) onError(new Error('script_load_failed'));
    };
    document.body.appendChild(script);
  }

  function claim(sid, onResult, onError) {
    jsonp(ENDPOINT, { action: 'claim', sid: sid }, onResult, onError);
  }

  window.ReLaunchPromo = {
    getSid: captureSid,
    setSid: setSid,
    claim: claim,
  };

  captureSid();

  /* ---------- UI wiring (single shared overlay, many star buttons) ---------- */

  function showStep(overlay, step) {
    overlay.querySelectorAll('[data-re-promo-step]').forEach(function (el) {
      el.style.display = el.getAttribute('data-re-promo-step') === step ? 'block' : 'none';
    });
  }

  var MIN_SPEND_BY_TIER = { '100': '250', '200': '400', '300': '500' };

  function runClaim(overlay, sid) {
    claim(
      sid,
      function (result) {
        if (!result || result.error) {
          showStep(overlay, 'email');
          var errorEl = overlay.querySelector('[data-re-promo-error]');
          errorEl.textContent = 'Something went wrong — please try again in a moment.';
          errorEl.style.display = 'block';
          return;
        }
        var amountEl = overlay.querySelector('[data-re-promo-prize-amount]');
        var linkEl = overlay.querySelector('[data-re-promo-apply-link]');
        var fineprintEl = overlay.querySelector("[data-re-promo-step='result'] [data-re-promo-fineprint]");
        var claimBtn = overlay.querySelector('[data-re-promo-claim]');
        var codeBox = overlay.querySelector('[data-re-promo-code-box]');

        amountEl.textContent = '$' + result.tier;
        linkEl.href = '/discount/' + encodeURIComponent(result.code) + '?redirect=/collections/all';

        var minSpend = MIN_SPEND_BY_TIER[result.tier];
        fineprintEl.textContent = minSpend
          ? 'Valid for seven days · Orders over $' + minSpend
          : 'Valid for seven days';

        // Reset claim/reveal UI in case this overlay is reused for a second visit.
        claimBtn.style.display = 'block';
        codeBox.style.display = 'none';
        linkEl.style.display = 'none';
        overlay.querySelector('[data-re-promo-code-text]').textContent = result.code;

        showStep(overlay, 'result');
      },
      function () {
        showStep(overlay, 'email');
        var errorEl = overlay.querySelector('[data-re-promo-error]');
        errorEl.textContent = 'Something went wrong — please try again in a moment.';
        errorEl.style.display = 'block';
      }
    );
  }

  function openPromo(overlay) {
    overlay.classList.add('is-open');
    var existingSid = captureSid();
    if (existingSid) {
      showStep(overlay, 'loading');
      runClaim(overlay, existingSid);
    } else {
      showStep(overlay, 'email');
    }
  }

  /**
   * The Our World background stars (.cura-ow-bg-star) are placed at
   * random x/y by curator.js on every page load — there's no fixed spot
   * to hardcode. Instead, once those real stars exist, move our clickable
   * star to sit exactly on top of one of them (biggest/brightest, for a
   * reasonable click target) rather than guessing a static position.
   */
  function alignToBackgroundStar() {
    var wrapper = document.querySelector('[data-re-promo-align-to-bg-star]');
    if (!wrapper) return;
    var bgStars = document.querySelectorAll('.cura-ow-bg-star');
    if (!bgStars.length) return;

    // Keep clear of the top/bottom/side edges — on hover the star scales up
    // to 1.8x with a wide glow, and the section clips overflow, so a star
    // too close to an edge would get visibly cut off.
    var MIN_PERCENT = 20;
    var MAX_PERCENT = 80;

    var best = null;
    var bestSize = -1;
    bgStars.forEach(function (star) {
      var top = parseFloat(star.style.top);
      var left = parseFloat(star.style.left);
      if (top < MIN_PERCENT || top > MAX_PERCENT || left < MIN_PERCENT || left > MAX_PERCENT) {
        return;
      }
      var size = parseFloat(star.style.width) || 0;
      if (size > bestSize) {
        bestSize = size;
        best = star;
      }
    });
    if (!best) return;

    wrapper.style.top = best.style.top;
    wrapper.style.left = best.style.left;
    wrapper.style.transform = 'translate(-50%, -50%)';
  }

  function initUI() {
    var overlay = document.querySelector('[data-re-promo-overlay]');
    if (!overlay) return;

    // The background starfield can populate slightly after our own script
    // runs, so try a couple of times rather than only once.
    alignToBackgroundStar();
    setTimeout(alignToBackgroundStar, 400);
    setTimeout(alignToBackgroundStar, 1500);

    document.querySelectorAll('[data-re-promo-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        // Some stars sit inside a product link (e.g. overlaid on a
        // Considered Pieces image) — stop the click from also navigating.
        e.preventDefault();
        e.stopPropagation();
        openPromo(overlay);
      });
    });

    overlay.querySelector('[data-re-promo-close]').addEventListener('click', function () {
      overlay.classList.remove('is-open');
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) overlay.classList.remove('is-open');
    });

    overlay.querySelector('[data-re-promo-submit]').addEventListener('click', function () {
      var emailInput = overlay.querySelector('[data-re-promo-email]');
      var email = (emailInput.value || '').trim();
      var errorEl = overlay.querySelector('[data-re-promo-error]');
      errorEl.style.display = 'none';
      if (!email || email.indexOf('@') === -1) {
        errorEl.textContent = 'Please enter a valid email.';
        errorEl.style.display = 'block';
        return;
      }
      setSid(email);
      showStep(overlay, 'loading');
      runClaim(overlay, email);
    });

    overlay.querySelector('[data-re-promo-claim]').addEventListener('click', function () {
      var claimBtn = overlay.querySelector('[data-re-promo-claim]');
      var codeBox = overlay.querySelector('[data-re-promo-code-box]');
      var linkEl = overlay.querySelector('[data-re-promo-apply-link]');
      claimBtn.style.display = 'none';
      codeBox.style.display = 'flex';
      linkEl.style.display = 'block';
    });

    overlay.querySelector('[data-re-promo-copy]').addEventListener('click', function () {
      var copyBtn = overlay.querySelector('[data-re-promo-copy]');
      var code = overlay.querySelector('[data-re-promo-code-text]').textContent;
      var done = function () {
        var original = 'Copy code';
        copyBtn.textContent = 'Copied';
        setTimeout(function () {
          copyBtn.textContent = original;
        }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, done);
      } else {
        done();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUI);
  } else {
    initUI();
  }
})();
